/**
 * @fileoverview Sticky header for the public landing page.
 *
 * Brand mark on the left (overflows the slim navbar to give the wordmark
 * presence), in-page anchor nav in the middle, and a theme toggle +
 * palette drawer + "Admin login" CTA on the right. The palette state is
 * local because each opener (Auth, Admin shell, here) owns its own
 * drawer — they share the same component, not the same state.
 *
 * Extracted verbatim out of the original Landing/index.tsx.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { LogIn } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ThemeToggle } from '@/components/theme-toggle'
import { ThemeDrawer, ThemeDrawerTrigger } from '@/components/theme-drawer'
import { LOGO_SRC } from '../constants'

export function SiteHeader() {
  // Local state for the colour-palette drawer. Identical pattern to the
  // Auth screen + admin shell, so the drawer behaves the same way no
  // matter where the visitor opens it from.
  const [themeDrawerOpen, setThemeDrawerOpen] = useState(false)
  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/60 bg-background/70 backdrop-blur supports-[backdrop-filter]:bg-background/50 dark:bg-background/75">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:h-18 sm:px-6 lg:h-20 lg:px-8 overflow-visible">
        <Link to="/" className="flex items-center overflow-visible" aria-label="Future Protea — home">
          {/* Wordmark is baked into the logo image, so no separate text
              label next to it. Logo is sized BIGGER than the header
              chrome and allowed to overflow above + below via the
              parent's `overflow-visible` — keeps the navbar slim while
              giving the brand mark real presence (matching the
              reference). The image's natural transparent padding
              softens the overflow into the rest of the page so the
              border line below the header isn't bisected harshly. */}
          <img
            src={LOGO_SRC}
            alt="Future Protea"
            className="h-20 w-auto sm:h-24 lg:h-28"
          />
        </Link>

        <nav className="hidden items-center gap-6 text-sm font-semibold text-muted-foreground md:flex lg:gap-8 lg:text-[15px]">
          <a href="#features"     className="hover:text-foreground transition-colors">Features</a>
          <a href="#mobile"       className="hover:text-foreground transition-colors">Mobile app</a>
          <a href="#how-it-works" className="hover:text-foreground transition-colors">How it works</a>
          <a href="#download"     className="hover:text-foreground transition-colors">Download</a>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Theme toggle + colour-palette picker — same components the
              Auth + Admin shell use, so the visitor can flip dark / light
              AND swap the accent palette from the landing page. The
              choice persists across the rest of the product via the
              `ColorThemeProvider`. */}
          <ThemeToggle />
          <ThemeDrawerTrigger onClick={() => setThemeDrawerOpen(true)} />
          <ThemeDrawer open={themeDrawerOpen} onOpenChange={setThemeDrawerOpen} />
          {/* The "Get the app" APK button used to sit after this CTA. The
              APK is untouched — the Download section, the hero CTA and the
              footer still link to it; only this header shortcut is gone. */}
          <Button asChild variant="ghost" size="sm" className="text-sm font-semibold">
            <Link to="/login">
              <LogIn className="h-4 w-4" />
              Admin login
            </Link>
          </Button>
        </div>
      </div>
    </header>
  )
}
